import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { KeyRound, UserRound, Hand, GraduationCap, Target, Trophy, Mic, Check, ArrowRight, Volume2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TypingBubble from "../components/TypingBubble";
import DiamondField from "../components/onboarding/DiamondField";
import { StepHeader, StepArt, StepTitle, StepPanel, PrimaryBtn, Chip } from "../components/onboarding/StepShell";

const GRAD = ["2026", "2027", "2028", "2029", "2030", "Already graduated"];
const PRESSURE = ["At the plate", "On the mound", "Late innings", "With runners on", "When scouts watch", "After a mistake"];
const GOALS = ["Go professional", "Play in college", "Stay calm under pressure", "Bounce back faster", "More confidence at the plate"];
const GUIDES = [
  { id: "coach_laura", name: "Coach Laura", desc: "Female · enthusiastic" },
  { id: "coach_conner", name: "Coach Conner", desc: "Male · energetic" },
  { id: "calm", name: "Calm", desc: "Soothing & steady" },
  { id: "focused", name: "Focused", desc: "Sharp & grounding" },
  { id: "uplifting", name: "Uplifting", desc: "Bright & warm" },
];
const BUBBLE_STEPS = { 0: () => "Hey! I'm your MindMoves guide. Let's build your first session — it takes about a minute.",
  2: (a) => `Great to meet you, ${a.name}. Let's dial in your game.` };

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [bubbleDone, setBubbleDone] = useState(false);
  const [a, setA] = useState({ name: user?.name || "", goals: [], pressures: [] });
  const [text, setText] = useState("");

  const bubble = BUBBLE_STEPS[step]?.(a);
  const ready = !bubble || bubbleDone;
  const go = (s) => { setBubbleDone(false); setText(""); setStep(s); };
  const advance = (patch) => { if (patch) setA((p) => ({ ...p, ...patch })); go(step + 1); };
  const toggleGoal = (g) => setA((p) => ({ ...p, goals: p.goals.includes(g) ? p.goals.filter((x) => x !== g) : [...p.goals, g] }));
  const togglePressure = (x) => setA((p) => ({ ...p, pressures: p.pressures.includes(x) ? p.pressures.filter((y) => y !== x) : [...p.pressures, x] }));

  const finish = async (guide) => {
    setA((p) => ({ ...p, guide }));
    go(9);
    try {
      const { data } = await api.post("/onboarding/complete", {
        team_code: a.team_code || null, name: a.name, sport: a.sport, position: a.position, throws: a.throws, bats: a.bats,
        grad_year: a.grad_year, pressure: a.pressure, goals: a.goals, goals_freetext: a.goals_freetext || "", guide,
      });
      await refresh();
      toast.success(data?.bundle?.hero?.pending ? "You're in! Coach is finalizing your first custom session." : "Your first session is ready!");
      navigate("/home", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate. Try again.");
      go(8);
    }
  };

  const body = () => {
    switch (step) {
      case 0: return (<>
        <StepArt icon={KeyRound} />
        <StepTitle title="Do you have a team code?" hint="Team codes unlock free sessions from your program. No code? No problem." />
        <div className="space-y-2.5">
          <input data-testid="onb-teamcode-input" value={text} onChange={(e) => setText(e.target.value.toUpperCase())} placeholder="e.g. BEARS"
            className="w-full px-4 py-3.5 rounded-2xl bg-white border border-slate-300 focus:border-royal outline-none uppercase tracking-widest text-center font-bold" />
          <PrimaryBtn data-testid="onb-teamcode-continue" disabled={!text} onClick={() => advance({ team_code: text })}>Continue</PrimaryBtn>
          <button data-testid="onb-teamcode-skip" onClick={() => advance({ team_code: null })}
            className="w-full text-slate-500 font-semibold py-2 text-sm hover:text-navy transition">I don't have a code</button>
        </div>
      </>);
      case 1: return (<>
        <StepArt icon={UserRound} />
        <StepTitle title="What should we call you?" hint="Your guide will use your name throughout your sessions." />
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) advance({ name: text.trim() }); }} className="flex gap-2">
          <input data-testid="onb-name-input" value={text} onChange={(e) => setText(e.target.value)} autoFocus placeholder="Your first name"
            className="flex-1 px-4 py-3.5 rounded-2xl bg-white border border-slate-300 focus:border-royal outline-none" />
          <button data-testid="onb-name-continue" type="submit" disabled={!text.trim()}
            className="bg-royal text-white px-5 rounded-2xl disabled:opacity-40 active:scale-95 transition"><ArrowRight size={20} /></button>
        </form>
      </>);
      case 2: return (<>
        <StepTitle title="Which do you play?" />
        <div className="grid grid-cols-2 gap-3">
          {["Baseball", "Softball"].map((s) => (
            <button key={s} data-testid={`onb-sport-${s.toLowerCase()}`} onClick={() => advance({ sport: s.toLowerCase() })}
              className="bg-white border border-slate-200 hover:border-royal rounded-3xl p-5 flex flex-col items-center gap-3 active:scale-[0.97] transition shadow-sm">
              <span className={`w-14 h-14 rounded-full border-[3px] ${s === "Baseball" ? "bg-white border-slate-200" : "bg-yellow-300 border-yellow-400"} relative overflow-hidden`}>
                <span className="absolute inset-y-0 left-2 w-4 border-x-2 border-red-400 rounded-[50%]" />
              </span>
              <span className="font-bold text-navy">{s}</span>
            </button>
          ))}
        </div>
      </>);
      case 3: return (<>
        <StepTitle title="Where do you play most?" hint="Tap your primary position on the field." />
        <DiamondField value={a.position} onSelect={(p) => advance({ position: p })} />
      </>);
      case 4: return (<>
        <StepArt icon={Hand} />
        <StepTitle title="How do you throw and bat?" />
        <div className="space-y-4">
          {[["Throws", "throws", ["L", "R"]], ["Bats", "bats", ["L", "R", "Switch"]]].map(([label, key, opts]) => (
            <div key={key}>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">{label}</div>
              <div className="flex gap-2">
                {opts.map((t) => <Chip key={t} active={a[key] === t} data-testid={`onb-${key}-${t}`} onClick={() => setA((p) => ({ ...p, [key]: t }))}>{t}</Chip>)}
              </div>
            </div>
          ))}
          <PrimaryBtn data-testid="onb-throwsbats-continue" disabled={!a.throws || !a.bats} onClick={() => advance()}>Continue</PrimaryBtn>
        </div>
      </>);
      case 5: return (<>
        <StepArt icon={GraduationCap} />
        <StepTitle title="When do you graduate?" hint="This helps us pace your development plan." />
        <div className="flex flex-wrap justify-center gap-2">
          {GRAD.map((g) => <Chip key={g} data-testid={`onb-grad-${g}`} onClick={() => advance({ grad_year: g })}>{g}</Chip>)}
        </div>
      </>);
      case 6: return (<>
        <StepArt icon={Target} />
        <StepTitle title="When does the pressure hit hardest?" hint="Real talk — pick every moment that applies. Your first session is built around these." />
        <div className="space-y-3">
          <div className="flex flex-wrap justify-center gap-2">
            {PRESSURE.map((p) => (
              <Chip key={p} active={a.pressures.includes(p)} data-testid={`onb-pressure-${p}`} onClick={() => togglePressure(p)}>
                {a.pressures.includes(p) && <Check size={14} className="inline mr-1 -mt-0.5" />}{p}
              </Chip>
            ))}
          </div>
          <PrimaryBtn data-testid="onb-pressure-continue" disabled={a.pressures.length === 0} onClick={() => advance({ pressure: a.pressures.join(", ") })}>Continue</PrimaryBtn>
        </div>
      </>);
      case 7: return (<>
        <StepArt icon={Trophy} />
        <StepTitle title="What are you chasing?" hint="Pick everything that fits." />
        <div className="space-y-3">
          <div className="flex flex-wrap justify-center gap-2">
            {GOALS.map((g) => (
              <Chip key={g} active={a.goals.includes(g)} data-testid={`onb-goal-${g}`} onClick={() => toggleGoal(g)}>
                {a.goals.includes(g) && <Check size={14} className="inline mr-1 -mt-0.5" />}{g}
              </Chip>
            ))}
          </div>
          <input data-testid="onb-goal-freetext" value={a.goals_freetext || ""} onChange={(e) => setA((p) => ({ ...p, goals_freetext: e.target.value }))}
            placeholder="Anything else? (optional)" className="w-full px-4 py-3 rounded-2xl bg-white border border-slate-300 focus:border-royal outline-none text-sm" />
          <PrimaryBtn data-testid="onb-goals-continue" disabled={a.goals.length === 0} onClick={() => advance()}>Continue</PrimaryBtn>
        </div>
      </>);
      case 8: return (<>
        <StepArt icon={Mic} />
        <StepTitle title="Who should guide your sessions?" hint="You can change your guide anytime in your profile." />
        <div className="space-y-2.5">
          {GUIDES.map((g) => (
            <button key={g.id} data-testid={`onb-guide-${g.id}`} onClick={() => finish(g.id)}
              className="w-full flex items-center gap-3 bg-white border border-slate-200 hover:border-royal rounded-2xl p-3.5 text-left active:scale-[0.98] transition shadow-sm">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-royal to-turq flex items-center justify-center text-white"><Volume2 size={20} /></div>
              <div className="flex-1"><div className="font-bold text-navy">{g.name}</div><div className="text-xs text-slate-500">{g.desc}</div></div>
              <ArrowRight size={18} className="text-slate-400" />
            </button>
          ))}
        </div>
      </>);
      default: return (
        <div className="flex flex-col items-center gap-6" data-testid="onb-crafting">
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 rounded-full bg-royal/30 animate-pulse-ring" />
            <div className="absolute inset-0 rounded-full bg-royal/20 animate-pulse-ring" style={{ animationDelay: "1s" }} />
            <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-royal to-turq flex items-center justify-center shadow-xl">
              <img src="/mindmoves-mark-white.png" alt="" className="w-11 h-11 object-contain" />
            </div>
          </div>
          <TypingBubble text={`Perfect, ${a.name}. Writing your first MindMoves and preparing ${GUIDES.find((g) => g.id === a.guide)?.name || "your guide"}'s voice… about 30 seconds.`} />
        </div>
      );
    }
  };

  return (
    <div className="mm-frame mm-glow relative flex flex-col" data-testid="onboarding-page">
      {step < 9 && <StepHeader step={step} onBack={() => go(step - 1)} />}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] overflow-y-auto mm-scroll">
        <AnimatePresence mode="wait">
          <StepPanel step={step}>
            {bubble && step < 9 && (
              <div className="mb-7"><TypingBubble key={`b-${step}`} text={bubble} testId={`mm-bubble-${step}`} onDone={() => setBubbleDone(true)} /></div>
            )}
            <div className={`transition-all duration-500 ${ready ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`}>
              {body()}
            </div>
          </StepPanel>
        </AnimatePresence>
      </div>
    </div>
  );
}
